/**
 * Module summary:
 * This file loads assessment definitions from either Firestore or local JSON
 * files. It also caches recently used results and reshapes Firebase node
 * documents into the flat category/question structure expected by the form UI.
 */

import {
Assessment,
AssessmentRegistry,
AssessmentRegistryEntry,
Question,
QuestionType,
Category
} from '../../types/assessment';

import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { getFirebaseDb } from '../firebaseConfig';

const DATA_BASE_PATH = '/data';
const DATA_PROVIDER = process.env.REACT_APP_DATA_PROVIDER || 'firebase';

// Two-tier cache: in-memory + localStorage for persistence across page reloads
const cache = new Map<string, any>();
const CACHE_DURATION = 60 * 60 * 1000; // 60 minutes (reduced Firestore reads)
const LS_CACHE_PREFIX = 'macha.assessmentCache.';

interface CacheEntry<T> {
data: T;
timestamp: number;
}

function getCached<T>(key: string): T | null {
// Check the in-memory cache first because it is the fastest way to reuse data
// during one browser session.
const entry = cache.get(key) as CacheEntry<T> | undefined;
if (entry) {
  // Drop stale entries so users eventually receive updated assessment content.
  if (Date.now() - entry.timestamp > CACHE_DURATION) {
    cache.delete(key);
  } else {
    return entry.data;
  }
}

// Fall back to localStorage so a refresh can still reuse a recent fetch.
try {
  const stored = localStorage.getItem(LS_CACHE_PREFIX + key);
  if (stored) {
    const parsed = JSON.parse(stored) as CacheEntry<T>;
    if (Date.now() - parsed.timestamp <= CACHE_DURATION) {
      // Promote the persisted entry back into memory so later reads stay fast.
      cache.set(key, parsed);
      return parsed.data;
    }
    localStorage.removeItem(LS_CACHE_PREFIX + key);
  }
} catch (e) { console.warn('Assessment cache read failed:', e); }

return null;
}

function setCached<T>(key: string, data: T): void {
const entry: CacheEntry<T> = { data, timestamp: Date.now() };
// Save to memory so the current tab can reuse the data without reparsing JSON.
cache.set(key, entry);
try {
  // Save to localStorage so the next reload can restore the same assessment quickly.
  localStorage.setItem(LS_CACHE_PREFIX + key, JSON.stringify(entry));
} catch { /* ignore quota errors */ }
}

/** ---------- Firebase Helpers ---------- */

function getDb() {
// Route every Firestore call through the shared Firebase bootstrap file.
return getFirebaseDb();
}

type NodeType = 'category' | 'subcategory' | 'question';

interface FirebaseNode {
assessmentId: string;
version?: string;
type: NodeType;
parentId: string | null;
key: string;
title?: string;
text?: string;
order?: number;
active?: boolean;
questionType?: string;
required?: boolean;
hasCommentField?: boolean;
commentPrompt?: string;
polarityValidated?: boolean;
polarityFixed?: boolean;
answerMeaningYes?: string;
answerMeaningNo?: string;
manualReview?: boolean;
manualReviewReason?: string;
tags?: string[];
severityLevel?: 'low' | 'medium' | 'high' | 'critical';
severityWeight?: number;
}

function sortByOrder<T extends { order?: number }>(arr: T[]): T[] {
// Sort records by the optional order field so categories and questions render
// in the same authored order users expect.
return [...arr].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
}

function toQuestionType(value?: string): QuestionType {
const v = (value || '').toLowerCase();
if (
v === 'boolean' ||
v === 'scale' ||
v === 'text' ||
v === 'select' ||
v === 'multiselect' ||
v === 'comment' ||
v === 'file'
) {
return v;
}
// Default unknown values to boolean so the UI falls back to the safest supported control.
return 'boolean';
}

/** ---------- LOCAL (existing behavior) ---------- */

async function loadAssessmentRegistryLocal(): Promise<AssessmentRegistry> {
// Fetch the static registry file from the public data folder when local mode is enabled.
const response = await fetch(`${DATA_BASE_PATH}/assessments/index.json`);
if (!response.ok) {
throw new Error(`Failed to load registry: ${response.status}`);
}
return await response.json();
}

async function loadAssessmentLocal(assessmentId: string): Promise<Assessment | null> {
// Fetch one assessment JSON file directly from the public folder in local mode.
const response = await fetch(`${DATA_BASE_PATH}/assessments/${assessmentId}.json`);
if (!response.ok) {
throw new Error(`Failed to load assessment: ${response.status}`);
}

const data = await response.json();

// Basic validation stops malformed files from reaching the form renderer.
if (!data.id || !data.categories || !Array.isArray(data.categories)) {
throw new Error('Invalid assessment structure');
}

return data as Assessment;
}

/** ---------- FIREBASE ---------- */

async function loadAssessmentRegistryFirebase(): Promise<AssessmentRegistry> {
const db = getDb();
// Query the top-level assessments collection so the UI can show an assessment picker.
const snap = await getDocs(query(collection(db, 'assessments')));

const assessments: AssessmentRegistryEntry[] = snap.docs.map((d): AssessmentRegistryEntry => {
const v = d.data() as any;
return {
// Fill every registry field with a fallback so incomplete Firestore documents
// still produce a usable card in the UI.
id: d.id,
title: v.title || d.id,
description: v.description || '',
version: v.version || '1.0.0',
lastUpdated: v.lastUpdated || new Date().toISOString(),
estimatedDuration: Number(v.estimatedDuration || 30),
fileName: `${d.id}.json`
};
});

return { assessments };
}

async function loadAssessmentFirebase(assessmentId: string): Promise<Assessment | null> {
const db = getDb();
const assessmentDoc = await getDoc(doc(db, 'assessments', assessmentId));
const assessmentMeta = assessmentDoc.exists() ? assessmentDoc.data() as any : null;
// Pull the active assessment nodes for one assessment so the client can rebuild
// the questionnaire hierarchy from normalized Firestore documents.
const nodesSnap = await getDocs(
query(
collection(db, 'assessmentNodes'),
where('assessmentId', '==', assessmentId),
where('active', '==', true)
)
);

const nodes: FirebaseNode[] = nodesSnap.docs.map((d) => d.data() as FirebaseNode);
if (!nodes.length) return null;

// Split the mixed node list by role so later mapping logic is easier to follow.
const categories = sortByOrder(nodes.filter((n) => n.type === 'category'));
const subcategories = sortByOrder(nodes.filter((n) => n.type === 'subcategory'));
const questions = sortByOrder(nodes.filter((n) => n.type === 'question'));

// NOTE: Your Category type expects questions[] directly (no nested subcategories).
// So we flatten each category's subcategory questions into one category.questions[].
const mappedCategories: Category[] = categories.map((cat) => {
const catSubs = subcategories.filter((s) => s.parentId === cat.key);

const catQuestions: Question[] = sortByOrder(
// Keep only questions whose parent subcategory belongs to this category so
// each section receives only its own questions.
questions.filter((q) => catSubs.some((s) => s.key === q.parentId))
).map((q) => {
const parentSub = catSubs.find((s) => s.key === q.parentId);

return {
// Convert each Firebase question node into the exact shape consumed by the React form.
id: q.key,
type: toQuestionType(q.questionType),
text: q.text || q.key,
helpText: parentSub?.title ? `Section: ${parentSub.title}` : undefined,
required: !!q.required,
hasCommentField: !!q.hasCommentField,
commentPrompt: q.commentPrompt || 'Add additional details (optional).',
polarityValidated: q.polarityValidated,
polarityFixed: q.polarityFixed,
answerMeaning: {
  yes: q.answerMeaningYes,
  no: q.answerMeaningNo,
},
manualReview: q.manualReview,
manualReviewReason: q.manualReviewReason,
tags: q.tags,
severityLevel: q.severityLevel,
severityWeight: q.severityWeight,
};
});

return {
id: cat.key,
title: cat.title || cat.key,
description: '',
questions: catQuestions
};
});

const assessment: Assessment = {
id: assessmentId,
version: assessmentMeta?.version || categories[0]?.version || '1.0.0',
metadata: {
title: assessmentMeta?.title || assessmentId,
description: assessmentMeta?.description || '',
lastUpdated: assessmentMeta?.lastUpdated || new Date().toISOString(),
estimatedDuration: Number(assessmentMeta?.estimatedDuration || 30),
totalQuestions: mappedCategories.reduce((sum, category) => sum + category.questions.length, 0),
categories: mappedCategories.length,
},
categories: mappedCategories
};

return assessment;
}

/** ---------- Public API ---------- */

export async function loadAssessmentRegistry(): Promise<AssessmentRegistry> {
const cacheKey = `assessment-registry-${DATA_PROVIDER}`;
// Cache by provider so local-mode data does not collide with Firebase-mode data.
const cached = getCached<AssessmentRegistry>(cacheKey);
if (cached) return cached;

try {
const data =
// Choose the data source at runtime so the same UI can work online and offline.
DATA_PROVIDER === 'firebase'
? await loadAssessmentRegistryFirebase()
: await loadAssessmentRegistryLocal();

setCached(cacheKey, data);
return data;
} catch (error) {
console.error('Error loading assessment registry:', error);
return { assessments: [] };
}
}

export async function loadAssessment(assessmentId: string): Promise<Assessment | null> {
const cacheKey = `assessment-${DATA_PROVIDER}-${assessmentId}`;
// Cache each assessment by id so reopening a form does not repeat the full fetch and transform path.
const cached = getCached<Assessment>(cacheKey);
if (cached) return cached;

try {
const data =
DATA_PROVIDER === 'firebase'
? await loadAssessmentFirebase(assessmentId)
: await loadAssessmentLocal(assessmentId);

if (data) setCached(cacheKey, data);
return data;
} catch (error) {
console.error(`Error loading assessment ${assessmentId}:`, error);
return null;
}
}

export async function getAssessmentMetadata(
assessmentId: string
): Promise<AssessmentRegistryEntry | null> {
const registry = await loadAssessmentRegistry();
return registry.assessments.find((a) => a.id === assessmentId) || null;
}

export async function preloadAssessments(assessmentIds: string[]): Promise<void> {
// Preload all requested assessments in parallel so navigation feels faster later.
await Promise.all(assessmentIds.map((id) => loadAssessment(id)));
}

export function clearAssessmentCache(): void {
cache.clear();
try {
  const keysToRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    // Walk every localStorage key so only assessment-cache entries are removed.
    const key = localStorage.key(i);
    if (key?.startsWith(LS_CACHE_PREFIX)) keysToRemove.push(key);
  }
  keysToRemove.forEach(k => localStorage.removeItem(k));
} catch { /* ignore */ }
}
